import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SentryErrorBoundary } from "@/lib/sentry";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Inventory from "./pages/Inventory";
import Wholesale from "./pages/Wholesale";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import TestPage from "./pages/TestPage";
import PageLoader from "./components/PageLoader";
import { NavigationTracker } from "./components/NavigationTracker";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on auth errors (401/403)
        if (error?.status === 401 || error?.status === 403) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
    },
    mutations: {
      retry: 1, // 1 retry attempt for mutations
    },
  },
});

const App = () => {
  return (
  <SentryErrorBoundary fallback={<ErrorBoundary children={null} />}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <NavigationTracker />
           <Routes>
             <Route path="/" element={
               <ProtectedRoute>
                 <Suspense fallback={<PageLoader />}>
                   <Dashboard />
                 </Suspense>
               </ProtectedRoute>
             } />
             <Route path="/auth" element={<Auth />} />
             <Route path="/landing" element={<Index />} />
             <Route path="/dashboard" element={
               <ProtectedRoute>
                 <Suspense fallback={<PageLoader />}>
                   <Dashboard />
                 </Suspense>
               </ProtectedRoute>
             } />
            <Route path="/inventory" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <Inventory />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/wholesale" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <Wholesale />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <Reports />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/test" element={<TestPage />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    {import.meta.env.DEV && (
      <ReactQueryDevtools initialIsOpen={false} />
    )}
  </QueryClientProvider>
  </SentryErrorBoundary>
  );
};

export default App;
